import { useEffect, useState } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import AdminPostCard from "../components/AdminPostCard";
import {
  getGlobalFeedPage,
  GLOBAL_FEED_PAGE_SIZE,
  GlobalFeedEntry,
} from "../services/socialMonitoringService";

const SocialMonitoringPage: React.FC = () => {
  const [entries, setEntries] = useState<GlobalFeedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const page = await getGlobalFeedPage();
        setEntries(page);
        setHasMore(page.length >= GLOBAL_FEED_PAGE_SIZE);
      } catch (err: any) {
        console.error("Error loading global feed:", err);
        setError(err.message || "Failed to load community feed");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const page = await getGlobalFeedPage();
      setEntries(page);
      setHasMore(page.length >= GLOBAL_FEED_PAGE_SIZE);
    } catch (err: any) {
      console.error("Error refreshing global feed:", err);
      setError(err.message || "Failed to load community feed");
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    const lastTs = entries[entries.length - 1]?.postTimestamp?.toDate?.();
    if (!lastTs) return;
    try {
      setLoadingMore(true);
      const page = await getGlobalFeedPage(lastTs);
      setEntries((prev) => [...prev, ...page]);
      setHasMore(page.length >= GLOBAL_FEED_PAGE_SIZE);
    } catch (err: any) {
      console.error("Error loading more feed entries:", err);
      setError(err.message || "Failed to load more posts");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <DashboardWrapper>
      <div className="w-full flex flex-col gap-6 p-6 h-[calc(100vh-64px)] overflow-hidden">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading">Social Monitoring</h1>
            <p className="text-sm text-gray-500 mt-1">
              Public posts from the Peak Community global feed.
            </p>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-ghost gap-2"
            onClick={handleRefresh}
            disabled={loading}
          >
            <ArrowPathIcon className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <span className="loading loading-spinner loading-sm" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center p-6">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex items-center justify-center p-6">
              <p className="text-sm text-gray-500">
                No public posts in the global feed yet. New public posts will appear
                here after the feed Cloud Function is deployed.
              </p>
            </div>
          ) : (
            <div className="max-w-xl mx-auto w-full pb-8">
              {entries.map((entry) =>
                entry.post ? (
                  <AdminPostCard key={entry.id} post={entry.post} />
                ) : (
                  <div
                    key={entry.id}
                    className="bg-base-100 shadow-sm mb-2 w-full rounded-xl border border-base-200 p-4 text-sm text-gray-500"
                  >
                    Post unavailable ({entry.authorId}/{entry.postId}) — it may have
                    been deleted.
                  </div>
                ),
              )}

              {hasMore && (
                <div className="flex justify-center pt-2">
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <span className="loading loading-spinner loading-xs" />
                    ) : (
                      "Load more"
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardWrapper>
  );
};

export default SocialMonitoringPage;
